import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { firstAccessiblePath, landingPagePath } from "@/lib/nav";
import ChoosePassword from "@/components/auth/ChoosePassword";

// `landing` marks the routes a signed-in user is sent to by default ("/" and
// "/overview", both page="dashboard"). Dashboard isn't on every Role, and an
// admin can also pick a specific launch page per person in Role Manager
// (users.landing_page) — so a landing route first honors that explicit
// choice if it's still open to them, then falls back to the first page in
// nav order they can open if Dashboard itself is blocked. Everywhere else,
// being blocked is the real answer and stays a "No access" screen.
export default function ProtectedRoute({ children, page, landing }) {
  const { user, loading, canAccess } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-[var(--ink-2)] font-heading text-sm tracking-wide">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Signed in with an old PIN or a temporary password: nothing opens until they choose their own password.
  if (user.must_set_password) return <ChoosePassword />;
  if (landing && !user.needs_role) {
    const chosen = landingPagePath(user.landing_page, canAccess);
    if (chosen && chosen !== location.pathname) return <Navigate to={chosen} replace />;
  }
  if (page && !canAccess(page)) {
    if (landing && !user.needs_role) {
      const fallback = firstAccessiblePath(canAccess, user.role === "admin");
      if (fallback && fallback !== location.pathname) return <Navigate to={fallback} replace />;
    }
    return (
      <div className="flex items-center justify-center h-full p-10 text-center">
        <div>
          <h2 className="font-heading text-2xl text-[var(--ink)] mb-2">{user.needs_role ? "No Role assigned" : "No access"}</h2>
          <p className="text-[var(--ink-2)] text-sm" data-testid="no-access-reason">
            {user.needs_role
              ? "Your account has no Role yet, so nothing is open to you. Ask an administrator to assign one in Role Manager."
              : "Your role doesn’t have permission for this page. Contact admin."}
          </p>
        </div>
      </div>
    );
  }
  return children;
}
